import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';
import { useParams } from 'react-router';
import { format as dfFormat } from 'date-fns';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { shakespeare } from '../../../vendor/mui-devias-kit/styles/theme/colors';
import { HydraCollection } from '../../types';

const getIntensityColor = (value: string | number) => {
  if (typeof value === 'string' && value !== '-') {
    if (value === 'Light') return shakespeare[200];
    if (value === 'Medium') return shakespeare[500];
    if (value === 'Heavy') return shakespeare[800];
    if (value === 'Spotting') return shakespeare[300];
    return shakespeare[400];
  }
  if (typeof value === 'number') {
    if (value === 0) return 'transparent';
    if (value <= 2) return shakespeare[100];
    if (value <= 4) return shakespeare[300];
    if (value <= 6) return shakespeare[500];
    if (value <= 8) return shakespeare[700];
    return shakespeare[900];
  }
  return 'transparent';
};

function toISO(d: Date) {
  return dfFormat(d, 'yyyy-MM-dd');
}

function enumerateDates(startISO: string, endISO: string) {
  const outDates: string[] = [];
  const outLabels: string[] = [];
  const start = new Date(startISO);
  const end = new Date(endISO);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start > end) return { isoDates: [], labels: [] };
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    outDates.push(toISO(d));
    outLabels.push(dfFormat(d, 'MMM d'));
  }
  return { isoDates: outDates, labels: outLabels };
}

export default function UserSymptoms() {
  const { id = '' } = useParams();
  const { getParams } = useModifiableParams({
    defaults: { timeframe: 'week' },
  });
  const params = getParams();
  const timeframe: 'week' | 'month' =
    params.timeframe === 'month' ? 'month' : 'week';
  let startDate: string | undefined = params.startDate;
  let endDate: string | undefined = params.endDate;
  if (!startDate || !endDate) {
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (timeframe === 'month' ? 29 : 6));
    startDate = toISO(start);
    endDate = toISO(today);
  }
  const { labels, isoDates } = enumerateDates(startDate!, endDate!);

  const apiMetricSymptoms =
    Api.MetricSymptom.useReadCollectionMetricSymptomsStartDateEndDate(
      id,
      startDate,
      endDate,
    );
  // @ts-expect-error backend schema is wrong
  const symptomResp = apiMetricSymptoms.data as HydraCollection<{
    date: string;
    items: Array<{
      symptom: { name: string };
      severity: number;
      avgSeverity: number | null;
    }>;
  }>;

  const cycleListApi = Api.MetricCycle.useReadCollectionMetricCycle(id, {
    'date[after]': startDate!,
    'date[before]': endDate!,
  });

  const today = new Date();
  const aYearAgo = new Date();
  aYearAgo.setFullYear(today.getFullYear() - 1);
  const cycleLengthsApi = Api.MetricCycle.useReadCollectionCycleLengths(id, {
    'date[after]': toISO(aYearAgo),
    'date[before]': toISO(today),
  });

  type Row = {
    name: string;
    values: Array<string | number>;
    avg: number | null;
  };
  const symptomIndex = new Map<string, number>();
  const rows: Row[] = [];
  const emptyVals = Array(isoDates.length).fill('-') as (number | '-')[];
  const periodRow: Row = { name: 'Period', values: [...emptyVals], avg: null };
  const outsideRow: Row = {
    name: 'Bleeding outside period',
    values: [...emptyVals],
    avg: null,
  };
  const flowOrder: Record<string, number> = {
    Heavy: 3,
    Medium: 2,
    Light: 1,
    Spotting: 0,
  };
  const normFlow = (s?: string | null): string | null => {
    if (!s) return null;
    const t = String(s).toLowerCase();
    if (t.includes('heavy')) return 'Heavy';
    if (t.includes('medium')) return 'Medium';
    if (t.includes('light')) return 'Light';
    if (t.includes('spot')) return 'Spotting';
    return null;
  };
  const cycles = cycleListApi.data?.member ?? [];
  for (const c of cycles) {
    const date: string | undefined = c?.date;
    const idx = date ? isoDates.indexOf(date) : -1;
    if (idx === -1) continue;
    const isPeriod = Boolean(c?.isPeriodBleeding);
    const flow = normFlow(c?.flowType) ?? (isPeriod ? 'Medium' : 'Spotting');
    const row = isPeriod ? periodRow : outsideRow;
    const current = row.values[idx];
    if (typeof current === 'string' || typeof current === 'number') {
      const curOrder =
        typeof current === 'string' ? (flowOrder[current] ?? -1) : -1;
      const nextOrder = flowOrder[flow] ?? -1;
      row.values[idx] = nextOrder >= curOrder ? flow : current;
    } else {
      row.values[idx] = flow;
    }
  }
  try {
    const members = symptomResp?.member ?? [];
    for (const entry of members) {
      const date: string | undefined = entry?.date;
      const idx = date ? isoDates.indexOf(date) : -1;
      if (idx === -1) continue;
      const items = entry?.items ?? [];
      for (const it of items) {
        const name: string | undefined = it?.symptom?.name;
        const sev: number | undefined =
          typeof it?.severity === 'number'
            ? it?.severity
            : Number(it?.severity);
        if (!name || sev == null || isNaN(sev)) continue;
        let rIndex = symptomIndex.get(name);
        if (rIndex == null) {
          rIndex = rows.length;
          symptomIndex.set(name, rIndex);
          rows.push({
            name,
            values: Array(isoDates.length).fill('-'),
            avg: null,
          });
        }
        const current = rows[rIndex].values[idx];
        // If multiple entries, choose highest severity for display
        rows[rIndex].values[idx] =
          typeof current === 'number' ? Math.max(current, sev) : sev;
      }
    }
    // compute averages
    for (const r of rows) {
      const nums = r.values.filter((v): v is number => typeof v === 'number');
      r.avg = nums.length
        ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2))
        : null;
    }
    // sort rows by average descending
    rows.sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1));
  } catch (_e) {
    // ignore mapping errors
  }

  // Insert cycle rows at the top
  rows.unshift(outsideRow);
  rows.unshift(periodRow);

  return (
    <Stack spacing={3}>
      <Card>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: '1px solid #DCDFE4',
            borderRadius: 1,
          }}
        >
          <Table
            sx={{
              width: 'fit-content',
              '& .MuiTableCell-root': { borderColor: '#DCDFE4' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>Symptom</TableCell>
                <TableCell align="center" sx={{ width: 113 }}>
                  Average
                </TableCell>
                {labels.map((label) => (
                  <TableCell key={label} align="center" sx={{ width: 113 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell align="center">
                    {row.avg != null ? row.avg.toFixed(2) : '-'}
                  </TableCell>
                  {row.values.map((value, idx) => {
                    const color = getIntensityColor(value);
                    return (
                      <TableCell
                        key={idx}
                        align="center"
                        sx={{
                          backgroundColor: color,
                          color:
                            color !== 'transparent' &&
                            color !== shakespeare[100] &&
                            color !== shakespeare[200]
                              ? 'white'
                              : 'inherit',
                          fontWeight:
                            color !== 'transparent' ? 'bold' : 'normal',
                        }}
                      >
                        {typeof value === 'number' ? value : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Card>
        <CardHeader
          title={<Typography variant="h6">Cycle Length</Typography>}
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={1}>
            {(() => {
              const items = cycleLengthsApi.data?.member ?? [];
              if (!items.length) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                );
              }
              const sorted = [...items].sort((a, b) =>
                a.startDate < b.startDate ? 1 : -1,
              );
              return sorted.map((cycle, index) => {
                const s = new Date(cycle.startDate);
                const e = new Date(cycle.endDate);
                const periodLabel = `${s.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} - ${e.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
                return (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Typography variant="subtitle1" sx={{ minWidth: '320px' }}>
                      {periodLabel}
                    </Typography>
                    <Typography variant="body1">{cycle.length} Days</Typography>
                  </Box>
                );
              });
            })()}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
